import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { AuthService } from './auth.service';
import { PlaybackService } from './playback.service';
import { PlayHistoryService } from './play-history.service';
import { UserService } from './user.service';
import { ViewStateService } from './view-state.service';
import { ConsoleBufferService } from './console-buffer.service';
import { StallMonitorService } from './stall-monitor.service';
import { WebPlaybackTrack } from '../../shared/models/spotify.models';

export interface BugReportPayload {
  reportId: string;
  description: string;
  openedAt: string;
  submittedAt: string;
  timezoneOffsetMinutes: number;
  context: any;
}

export interface BugReportResponse {
  success: boolean;
  reportId: string;
  serverTimestamp: string;
}

@Injectable({
  providedIn: 'root'
})
export class BugReportService {
  constructor(
    private http: HttpClient,
    private playbackService: PlaybackService,
    private authService: AuthService,
    private userService: UserService,
    private viewStateService: ViewStateService,
    private playHistoryService: PlayHistoryService,
    private stallMonitorService: StallMonitorService,
    private router: Router
  ) {}

  /**
   * Snapshot of app state to attach to a bug report. Synchronous state is
   * captured first so it reflects the moment of collection; async extras
   * (devices, volume) are best-effort with a short timeout.
   */
  async collectContext(): Promise<any> {
    const context: any = {};

    try {
      context.route = this.router.url;
      context.viewMode = this.viewStateService.isSimpleView ? 'simple' : 'full';

      const combined = this.playbackService.getCombinedPlaybackState();
      context.playback = combined ? {
        currentTrack: combined.currentTrack ? {
          id: combined.currentTrack.id,
          uri: combined.currentTrack.uri,
          name: combined.currentTrack.name,
          artists: combined.currentTrack.artists?.map(artist => artist.name) || [],
          album: combined.currentTrack.album?.name || null,
          duration_ms: combined.currentTrack.duration_ms
        } : null,
        isPlaying: combined.isPlaying,
        progress: combined.progress,
        duration: combined.duration,
        isWebSDKActive: combined.isWebSDKActive,
        shuffleState: combined.shuffleState,
        repeatMode: combined.repeatMode
      } : null;

      const sdkState = this.currentValue(this.playbackService.playbackState$);
      context.sdkState = sdkState ? {
        contextUri: sdkState.context?.uri || null,
        paused: sdkState.paused,
        position: sdkState.position,
        shuffle: sdkState.shuffle,
        repeat_mode: sdkState.repeat_mode,
        disallows: sdkState.disallows,
        track_window: {
          current: this.mapSdkTrack(sdkState.track_window?.current_track),
          next: (sdkState.track_window?.next_tracks || []).map(track => this.mapSdkTrack(track)),
          previous: (sdkState.track_window?.previous_tracks || []).map(track => this.mapSdkTrack(track))
        }
      } : null;

      context.playlistContextId = this.currentValue(this.playbackService.currentPlaylistContextId$);
      context.localDeviceId = this.playbackService.getDeviceId();
      context.playerReady = this.playbackService.isPlayerReady();
      context.sdkUnavailable = this.playbackService.sdkUnavailable;

      const diagnostics = this.playbackService.getRecoveryDiagnostics();
      const nowMs = Date.now();
      context.recoveryDiagnostics = {
        lastPlayerStateChangedAt: this.toIso(diagnostics.lastPlayerStateChangedAt),
        msSincePlayerStateChanged: diagnostics.lastPlayerStateChangedAt !== null ? nowMs - diagnostics.lastPlayerStateChangedAt : null,
        lastHealthCheckAt: this.toIso(diagnostics.lastHealthCheckAt),
        msSinceHealthCheck: diagnostics.lastHealthCheckAt !== null ? nowMs - diagnostics.lastHealthCheckAt : null,
        lastSuccessfulPollAt: this.toIso(diagnostics.lastSuccessfulPollAt),
        msSinceSuccessfulPoll: diagnostics.lastSuccessfulPollAt !== null ? nowMs - diagnostics.lastSuccessfulPollAt : null,
        // The health check's inputs: does the user want music playing, and how
        // many consecutive polls have contradicted that so far?
        intendedPlaying: diagnostics.intendedPlaying,
        healthStrikes: diagnostics.healthStrikes,
        recoveryHold: diagnostics.recoveryHold,
        // Web API's independent view — contrast with sdkState/livePlayerState
        // to spot phantom playback (SDK playing, API not).
        lastPolledPlayerState: diagnostics.lastPolledPlayerState ? {
          at: this.toIso(diagnostics.lastPolledPlayerState.at),
          msSince: nowMs - diagnostics.lastPolledPlayerState.at,
          isPlaying: diagnostics.lastPolledPlayerState.isPlaying,
          progressMs: diagnostics.lastPolledPlayerState.progressMs,
          trackId: diagnostics.lastPolledPlayerState.trackId,
          deviceId: diagnostics.lastPolledPlayerState.deviceId,
          isWebSDKActive: diagnostics.lastPolledPlayerState.isWebSDKActive
        } : null,
        // Did the SDK acknowledge the user's last transport command?
        lastCommand: diagnostics.lastCommand ? {
          command: diagnostics.lastCommand.command,
          issuedAt: this.toIso(diagnostics.lastCommand.issuedAt),
          msSinceIssued: nowMs - diagnostics.lastCommand.issuedAt,
          echoedAt: this.toIso(diagnostics.lastCommand.echoedAt),
          echoMs: diagnostics.lastCommand.echoedAt !== null ? diagnostics.lastCommand.echoedAt - diagnostics.lastCommand.issuedAt : null
        } : null,
        // Recent health evaluations (oldest first) — includes the silent
        // exits that never reach the console log.
        healthVerdicts: diagnostics.healthVerdicts.map(v => ({
          at: this.toIso(v.at),
          verdict: v.verdict,
          isPlaying: v.isPlaying,
          progressMs: v.progressMs,
          trackId: v.trackId
        })),
        // Recent Web API poll results (oldest first) — reconstructs exactly
        // what the health check saw around an incident.
        pollHistory: diagnostics.pollHistory.map(pollEntry => ({
          at: this.toIso(pollEntry.at),
          latencyMs: pollEntry.at - pollEntry.requestedAt,
          isPlaying: pollEntry.isPlaying,
          progressMs: pollEntry.progressMs,
          trackId: pollEntry.trackId,
          deviceId: pollEntry.deviceId,
          isWebSDKActive: pollEntry.isWebSDKActive
        })),
        // Mid-track stalls seen from the SDK (unprompted state event at a
        // frozen position) and how each ended — self-healed or recovered.
        stallEpisodes: diagnostics.stallEpisodes.map(episode => ({
          at: this.toIso(episode.at),
          trackId: episode.trackId,
          positionMs: episode.positionMs,
          outcome: episode.outcome,
          resolvedAfterMs: episode.resolvedAfterMs
        }))
      };

      // Media-element instrumentation: buffer levels and native stall events
      // with network context — explains WHY audio stalled, not just that it did.
      const stallDiag = this.stallMonitorService.getDiagnostics();
      context.stallMonitor = {
        mediaElementFound: stallDiag.mediaElementFound,
        samples: stallDiag.samples.map(s => ({
          at: this.toIso(s.at),
          currentTime: s.currentTime,
          bufferedAhead: s.bufferedAhead,
          readyState: s.readyState,
          paused: s.paused
        })),
        stalls: stallDiag.stalls.map(s => ({
          at: this.toIso(s.at),
          type: s.type,
          currentTime: s.currentTime,
          bufferedAhead: s.bufferedAhead,
          readyState: s.readyState,
          connection: s.connection,
          backendPingMs: s.backendPingMs,
          recoveredAfterMs: s.recoveredAfterMs
        }))
      };

      const userInfo = this.authService.getUserInfo();
      context.auth = {
        isAuthenticated: this.authService.isAuthenticated(),
        userId: userInfo?.id || null,
        displayName: userInfo?.display_name || null
      };
      context.userProduct = this.userService.getCurrentUser()?.product ?? null;

      context.recentPlays = this.playHistoryService.exportHistory().entries
        .slice(0, 10)
        .map(entry => ({
          trackName: entry.trackName,
          artistNames: entry.artistNames,
          playedAt: entry.playedAt,
          playlistId: entry.playlistId ?? null
        }));

      context.environment = {
        userAgent: navigator.userAgent,
        language: navigator.language,
        viewport: { width: window.innerWidth, height: window.innerHeight },
        screen: { width: screen.width, height: screen.height },
        online: navigator.onLine
      };

      context.consoleLogs = ConsoleBufferService.getBuffer();
    } catch (error) {
      context.collectError = error instanceof Error ? error.message : String(error);
    }

    const [devices, volume, liveProbe] = await Promise.all([
      this.bestEffort(this.playbackService.getAvailableDevices()),
      this.bestEffort(this.playbackService.getVolume()),
      this.playbackService.probeLiveState(3000) // self-bounded <=3s, never rejects — no bestEffort wrapper needed
    ]);
    context.devices = devices?.devices ?? null;
    context.volume = volume;
    context.livePlayerState = {
      outcome: liveProbe.outcome,
      elapsedMs: liveProbe.elapsedMs,
      state: liveProbe.state ? {
        contextUri: liveProbe.state.context?.uri || null,
        paused: liveProbe.state.paused,
        loading: liveProbe.state.loading ?? false,
        position: liveProbe.state.position,
        duration: liveProbe.state.duration,
        currentTrack: this.mapSdkTrack(liveProbe.state.track_window?.current_track)
      } : null
    };

    return context;
  }

  submit(payload: BugReportPayload): Observable<BugReportResponse> {
    return this.http.post<BugReportResponse>(`${environment.apiBaseUrl}/bug-report`, payload, {
      withCredentials: true
    });
  }

  private toIso(epochMs: number | null): string | null {
    return epochMs !== null ? new Date(epochMs).toISOString() : null;
  }

  private mapSdkTrack(track: WebPlaybackTrack | undefined): { id: string; name: string; artists: string[] } | null {
    if (!track) {
      return null;
    }
    return {
      id: track.id,
      name: track.name,
      artists: track.artists?.map(artist => artist.name) || []
    };
  }

  /**
   * Read the current value of a BehaviorSubject-backed observable synchronously.
   */
  private currentValue<T>(observable: Observable<T>): T | null {
    let value: T | null = null;
    observable.pipe(take(1)).subscribe(current => {
      value = current;
    });
    return value;
  }

  /**
   * Resolve to null instead of hanging/throwing — bug reporting must never block.
   */
  private bestEffort<T>(promise: Promise<T>, timeoutMs = 1500): Promise<T | null> {
    const timeout = new Promise<null>(resolve => setTimeout(() => resolve(null), timeoutMs));
    return Promise.race([promise.catch(() => null), timeout]);
  }
}
